<template><div><code-well-header>
  <dt-radio
    value="optionValue"
    label="Radio label"
  />
</code-well-header>
<!-- <component-combinator component-name="DtRadio" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Radio buttons are a common way to allow users to make a single selection from a list of options. Since only one radio button can be selected at a time (within the same group), each available choice must be its own item and label. Upon selection of a radio item in a group, the group cannot be easily reset to zero selections.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When users may only choose a single option out of a set of mutually exclusive choices.</li>
<li class="d-docsite--list-element">If the number of available options can fit onto a mobile screen.</li>
<li class="d-docsite--list-element">In place of <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink> element if there are few enough options (e.g. =7) and the design can support it.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Consider <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink> if users may have the option to select more than one.</li>
<li class="d-docsite--list-element">Consider a <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink> if you don’t have enough space to list out all available options.</li>
<li class="d-docsite--list-element">If users should be able to select zero of the options; radio elements are not “uncheckable.” A <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink> may be warranted.</li>
<li class="d-docsite--list-element">If there are too many options to display on a single view; consider a <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink> instead.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Users should be able to tap on or click either the text <code v-pre>label</code> or the radio element itself to select an option.</li>
<li class="d-docsite--list-element">Options that are listed vertically are easier to read than those listed horizontally. Horizontal listings can make it difficult to tell which label pertains to which radio button.</li>
<li class="d-docsite--list-element">Make sure selections are adequately spaced for touch screens.</li>
<li class="d-docsite--list-element">Use caution if you decide to set a default value as they cannot be unchecked. Setting a default value can discourage users from making conscious decisions, seem pushy, or alienate users who don’t fit into your assumptions. If you are unsure, leave nothing selected by default.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<code-well-header>
  <fieldset class="d-input-group__fieldset d-stack8">
    <dt-radio name="Value" value="Value" label="Radio label"/>
    <dt-radio name="Disabled" value="Disabled" label="Radio label thats been disabled" disabled/>
    <dt-radio name="CheckedDisabled" value="Checked" label="Radio label thats been disabled & checked" :model-value="true" disabled />
  </fieldset>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-radio-group">
      <div class="d-radio__input"><input name="Value" type="radio" class="d-radio" value="Value" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>Radio label</div>
      </div>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-radio-group d-radio-group--disabled">
      <div class="d-radio__input"><input name="Disabled" disabled="disabled" type="radio" class="d-radio" value="Disabled" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>Radio label thats been disabled</div>
      </div>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-radio-group d-radio-group--disabled">
      <div class="d-radio__input"><input name="CheckedDisabled" disabled="disabled" type="radio" class="d-radio" value="Checked" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>Radio label thats been disabled &amp; checked</div>
      </div>
    </div>
  </label>
</div>
'
vueCode='
<dt-radio name="Value" value="Value" label="Radio label"/>
<dt-radio name="Disabled" value="Disabled" label="Radio label thats been disabled" disabled/>
<dt-radio name="CheckedDisabled" value="Checked" label="Radio label thats been disabled & checked" :model-value="true" disabled />
'
showHtmlWarning />
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-well-header>
  <fieldset class="d-input-group__fieldset d-stack8">
    <legend class="d-label">Advanced missed call routing</legend>
    <dt-radio name="ValueWDesc" value="Value" label="To voicemail" description="So they can hear your voice"/>
    <dt-radio name="DisabledWDesc" value="Disabled" label="Disabled" description="With Description" disabled />
  </fieldset>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-radio-group">
      <div class="d-radio__input"><input name="ValueWDesc" type="radio" class="d-radio" value="Value" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>To voicemail</div>
        <div class="d-description">So they can hear your voice</div>
      </div>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-radio-group d-radio-group--disabled">
      <div class="d-radio__input"><input name="DisabledWDesc" type="radio" class="d-radio" value="Disabled" disabled="disabled" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>Disabled</div>
        <div class="d-description">With Description</div>
      </div>
    </div>
  </label>
</div>
'
vueCode='
<dt-radio name="ValueWDesc" value="Value" label="To voicemail" description="So they can hear your voice"/>
<dt-radio name="DisabledWDesc" value="Disabled" label="Disabled" description="With Description" disabled />
'
showHtmlWarning />
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<code-well-header>
  <fieldset class="d-input-group__fieldset d-stack8">
    <legend class="d-label">Advanced missed call routing</legend>
    <dt-radio
      name="ValidationMessages"
      value="Validation Message Warning"
      label="To voicemail"
      validation-state="warning"
      :messages="[{ message: `So they can hear your voice`, type: `warning` }]"
    />
    <dt-radio
      name="ValidationMessages"
      value="Validation Message Error"
      label="To a message (no voicemail)"
      validation-state="error"
      :messages="[{ message: `Because they probably don't need to leave a message anyway.`, type: `error` }]"
    />
    <dt-radio
      name="ValidationMessages"
      value="Validation Message Success"
      label="To a team member or room phone"
      validation-state="success"
      :messages="[{ message: `Because someone else might be able to talk to them.`, type: `success` }]"
    />
  </fieldset>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-radio-group">
      <div class="d-radio__input"><input name="ValidationMessageWarning" type="radio" class="d-radio d-radio--warning" value="Validation Message Warning" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>To voicemail</div>
        <div class="base-input__messages d-validation-message__container">
          <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--warning d-validation-message--warning"><p>So they can hear your voice</p></div>
        </div>
      </div>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-radio-group">
      <div class="d-radio__input"><input name="ValidationMessageError" type="radio" class="d-radio d-radio--error" value="Validation Message Error" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>To a message (no voicemail)</div>
        <div class="base-input__messages d-validation-message__container">
          <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--error d-validation-message--error">
            <p>Because they probably dont need to leave a message anyway.</p>
          </div>
        </div>
      </div>
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-radio-group">
      <div class="d-radio__input"><input name="ValidationMessageSuccess" type="radio" class="d-radio d-radio--success" value="Validation Message Success" /></div>
      <div class="d-radio__copy d-radio__label">
        <div>To a team member or room phone</div>
        <div class="base-input__messages d-validation-message__container">
          <div role="status" aria-live="polite" class="base-input__message d-validation-message base-input__message--success d-validation-message--success">
            <p>Because someone else might be able to talk to them.</p>
          </div>
        </div>
      </div>
    </div>
  </label>
</div>
'
vueCode='
<dt-radio
  name="ValidationMessageWarning"
  value="Validation Message Warning"
  label="To voicemail"
  validation-state="warning"
  :messages="[{ message: `So they can hear your voice`, type: `warning` }]"
/>
<dt-radio
  name="ValidationMessageError"
  value="Validation Message Error"
  label="To a message (no voicemail)"
  validation-state="error"
/>
<dt-radio
  name="ValidationMessageSuccess"
  value="Validation Message Success"
  label="To a team member or room phone"
  validation-state="success"
  :messages="[{ message: `Because someone else might be able to talk to them.`, type: `success` }]"
/>
'
showHtmlWarning />
<h3 id="with-slotted-label" tabindex="-1" class="d-docsite--header-3">With Slotted Label <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-label" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-radio
    ref="slottedLabel"
    name="ValueWSlot"
    value="Value"
  >
    With Slotted Label
  </dt-radio>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.slottedLabel'
vueCode='
<dt-radio
  name="ValueWSlot"
  value="Value"
>
  With Slotted Label
</dt-radio>
'
/>
<h3 id="with-slotted-description" tabindex="-1" class="d-docsite--header-3">With Slotted Description <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-description" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-radio
    ref="slottedDescription"
    name="ValueWSlottedDescription"
    value="Value"
    label="With"
  >
    <template #description>
      Slotted Description
    </template>
  </dt-radio>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.slottedDescription'
vueCode='
<dt-radio
  name="ValueWSlottedDescription"
  value="Value"
  label="With"
>
  <template #description>
    Slotted Description
  </template>
</dt-radio>
'
/>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="radio" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The best accessibility is semantic HTML. Most screen readers understand how to parse inputs if they’re correctly formatted. When it comes to radio input, there are a few things to keep in mind:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">All inputs should have an <code v-pre>id</code> attribute.</li>
<li class="d-docsite--list-element">Associate radio labels with their inputs using the <code v-pre>for</code> attribute. This correlates with the radio's <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">If you have a group of related radios, use a <code v-pre>fieldset</code> to group them and a <code v-pre>legend</code> to title the group. For further information, please visit Gov.UK’s article, <a href="https://accessibility.blog.gov.uk/2016/07/22/using-the-fieldset-and-legend-elements" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">&quot;Using the fieldset and legend elements&quot;</a>.</li>
<li class="d-docsite--list-element">If the input is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Input with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
<li class="d-docsite--list-element">Input with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="radio" /></div></template>


